import React, {FC, useContext, useEffect, useState} from "react";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormGroup,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Switch,
	TextField,
	Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {makeStyles} from "tss-react/mui";
import {
	App2NotificationContext,
	App2NotificationSeverity
} from "@/app2/shared/contexts/App2NotificationContext";
import {
	type QuestionSlim,
	type UpdateQuizQuestionBody,
	type UpdateQuizQuestionResponse,
	useUpdateQuizQuestion
} from "@/app2/shared/data";
import {palette} from "@/constants";

type EditableQuestionType =
	| "boolean_choice_questions"
	| "single_choice_questions"
	| "multiple_choice_questions";

interface EditQuizQuestionModalProps {
	open: boolean;
	question: QuestionSlim | null;
	onClose: () => void;
	onSaved: (question: QuestionSlim) => void;
}

const EDITABLE_TYPES = new Set<string>([
	"boolean_choice_questions",
	"single_choice_questions",
	"multiple_choice_questions"
]);

const formatQuestionType = (type: string): string =>
	type
		.replace(/_/g, " ")
		.replace(/questions/gi, "")
		.replace(/question/gi, "")
		.trim() || "Question";

const useStyles = makeStyles()((theme) => ({
	optionRow: {
		display: "grid",
		gridTemplateColumns: "1fr auto",
		gap: theme.spacing(1),
		alignItems: "center"
	},
	optionActions: {
		display: "flex",
		gap: theme.spacing(0.5)
	},
	addOptionRow: {
		display: "flex",
		gap: theme.spacing(1),
		alignItems: "center"
	},
	typeLabel: {
		color: palette.grey70,
		textTransform: "capitalize"
	}
}));

const normalizeStringList = (items: string[]): string[] =>
	items.map((item) => item.trim()).filter(Boolean);

export const EditQuizQuestionModal: FC<EditQuizQuestionModalProps> = ({
	open,
	question,
	onClose,
	onSaved
}) => {
	const {classes} = useStyles();
	const {notify} = useContext(App2NotificationContext);
	const updateQuestion = useUpdateQuizQuestion();
	const [title, setTitle] = useState("");
	const [hint, setHint] = useState("");
	const [boolAnswer, setBoolAnswer] = useState(false);
	const [options, setOptions] = useState<string[]>([]);
	const [correctAnswer, setCorrectAnswer] = useState("");
	const [correctAnswers, setCorrectAnswers] = useState<string[]>([]);
	const [newOption, setNewOption] = useState("");

	useEffect(() => {
		if (!question) {
			return;
		}
		setTitle(question.title ?? "");
		setHint(question.hint ?? "");
		setBoolAnswer(question.answer ?? false);
		setOptions(question.options ?? []);
		setCorrectAnswer(question.correctAnswer ?? "");
		setCorrectAnswers(question.correctAnswers ?? []);
		setNewOption("");
	}, [question]);

	if (!question) {
		return null;
	}

	const questionType = question.__type as EditableQuestionType;
	const isEditable = EDITABLE_TYPES.has(question.__type);
	const pending = updateQuestion.isPending;

	const replaceOption = (index: number, nextValue: string): void => {
		const oldValue = options[index] ?? "";
		setOptions((prev) => prev.map((option, i) => (i === index ? nextValue : option)));
		if (correctAnswer === oldValue) {
			setCorrectAnswer(nextValue);
		}
		setCorrectAnswers((prev) => prev.map((answer) => (answer === oldValue ? nextValue : answer)));
	};

	const moveOption = (index: number, direction: -1 | 1): void => {
		const nextIndex = index + direction;
		if (nextIndex < 0 || nextIndex >= options.length) {
			return;
		}
		setOptions((prev) => {
			const next = [...prev];
			const [item] = next.splice(index, 1);
			next.splice(nextIndex, 0, item);
			return next;
		});
	};

	const deleteOption = (index: number): void => {
		const removed = options[index];
		setOptions((prev) => prev.filter((_, i) => i !== index));
		if (correctAnswer === removed) {
			setCorrectAnswer("");
		}
		setCorrectAnswers((prev) => prev.filter((answer) => answer !== removed));
	};

	const addOption = (): void => {
		const value = newOption.trim();
		if (!value) {
			return;
		}
		if (options.some((option) => option.trim() === value)) {
			notify("Options must be unique", "Validation", App2NotificationSeverity.warning, 5000);
			return;
		}
		setOptions((prev) => [...prev, value]);
		setNewOption("");
	};

	const toggleCorrectAnswer = (option: string, checked: boolean): void => {
		setCorrectAnswers((prev) => {
			if (checked) {
				return prev.includes(option) ? prev : [...prev, option];
			}
			return prev.filter((answer) => answer !== option);
		});
	};

	const buildBody = (): UpdateQuizQuestionBody | null => {
		const trimmedTitle = title.trim();
		if (!trimmedTitle) {
			notify("Title is required", "Validation", App2NotificationSeverity.warning, 5000);
			return null;
		}

		if (questionType === "boolean_choice_questions") {
			return {
				title: trimmedTitle,
				hint: hint.trim(),
				answer: boolAnswer
			};
		}

		const trimmedOptions = normalizeStringList(options);
		if (trimmedOptions.length === 0) {
			notify("At least one option is required", "Validation", App2NotificationSeverity.warning, 5000);
			return null;
		}
		if (new Set(trimmedOptions).size !== trimmedOptions.length) {
			notify("Options must be unique", "Validation", App2NotificationSeverity.warning, 5000);
			return null;
		}

		if (questionType === "single_choice_questions") {
			const trimmedCorrect = correctAnswer.trim();
			if (!trimmedCorrect || !trimmedOptions.includes(trimmedCorrect)) {
				notify(
					"Select a correct answer from the options",
					"Validation",
					App2NotificationSeverity.warning,
					5000
				);
				return null;
			}
			return {
				title: trimmedTitle,
				hint: hint.trim(),
				options: trimmedOptions,
				correctAnswer: trimmedCorrect
			};
		}

		const trimmedCorrectAnswers = normalizeStringList(correctAnswers);
		if (trimmedCorrectAnswers.length === 0) {
			notify("Select at least one correct answer", "Validation", App2NotificationSeverity.warning, 5000);
			return null;
		}
		for (const answer of trimmedCorrectAnswers) {
			if (!trimmedOptions.includes(answer)) {
				notify(
					"All correct answers must be selected from the options",
					"Validation",
					App2NotificationSeverity.warning,
					5000
				);
				return null;
			}
		}
		return {
			title: trimmedTitle,
			hint: hint.trim(),
			options: trimmedOptions,
			correctAnswers: trimmedCorrectAnswers
		};
	};

	const handleSave = async (): Promise<void> => {
		if (!isEditable) {
			return;
		}
		const body = buildBody();
		if (!body) {
			return;
		}
		try {
			const saved: UpdateQuizQuestionResponse = await updateQuestion.mutateAsync({
				questionType: question.__type,
				questionId: question.id,
				body
			});
			notify("Question saved successfully", "Success", App2NotificationSeverity.success, 3000);
			onSaved({
				...question,
				...saved
			});
			onClose();
		} catch (error: unknown) {
			const message = error instanceof Error ? error.message : "Failed to save question";
			notify(message, "Error", App2NotificationSeverity.error, 5000);
		}
	};

	const optionEditor =
		questionType === "single_choice_questions" || questionType === "multiple_choice_questions" ? (
			<Stack spacing={1.5}>
				<Typography fontWeight={600}>Answers</Typography>
				{options.map((option, index) => (
					<Box className={classes.optionRow} key={index}>
						<TextField
							label={`Answer ${index + 1}`}
							value={option}
							onChange={(event) => replaceOption(index, event.target.value)}
							size="small"
							fullWidth
						/>
						<Box className={classes.optionActions}>
							<IconButton
								size="small"
								onClick={() => moveOption(index, -1)}
								disabled={index === 0}
								aria-label="Move answer up"
							>
								<ArrowUpwardIcon fontSize="small" />
							</IconButton>
							<IconButton
								size="small"
								onClick={() => moveOption(index, 1)}
								disabled={index === options.length - 1}
								aria-label="Move answer down"
							>
								<ArrowDownwardIcon fontSize="small" />
							</IconButton>
							<IconButton
								size="small"
								onClick={() => deleteOption(index)}
								aria-label="Delete answer"
							>
								<DeleteOutlineIcon fontSize="small" />
							</IconButton>
						</Box>
					</Box>
				))}
				<Box className={classes.addOptionRow}>
					<TextField
						label="New answer"
						value={newOption}
						onChange={(event) => setNewOption(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === "Enter") {
								event.preventDefault();
								addOption();
							}
						}}
						size="small"
						fullWidth
					/>
					<Button
						variant="outlined"
						startIcon={<AddIcon />}
						onClick={addOption}
						sx={{borderColor: palette.orange, color: palette.white}}
					>
						Add
					</Button>
				</Box>
				{questionType === "single_choice_questions" ? (
					<FormControl fullWidth size="small">
						<InputLabel id="edit-quiz-question-correct-answer-label">
							Correct answer
						</InputLabel>
						<Select
							labelId="edit-quiz-question-correct-answer-label"
							label="Correct answer"
							value={options.includes(correctAnswer) ? correctAnswer : ""}
							onChange={(event) => setCorrectAnswer(event.target.value)}
						>
							{options.map((option, index) => (
								<MenuItem key={`${index}-${option}`} value={option}>
									{option || `Answer ${index + 1}`}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				) : (
					<Box>
						<Typography fontWeight={600} sx={{mb: 1}}>
							Correct answers
						</Typography>
						<FormGroup>
							{options.map((option, index) => (
								<FormControlLabel
									key={`${index}-${option}`}
									control={
										<Checkbox
											checked={correctAnswers.includes(option)}
											onChange={(event) => toggleCorrectAnswer(option, event.target.checked)}
										/>
									}
									label={option || `Answer ${index + 1}`}
								/>
							))}
						</FormGroup>
					</Box>
				)}
			</Stack>
		) : null;

	return (
		<Dialog open={open} onClose={() => !pending && onClose()} fullWidth maxWidth="md">
			<DialogTitle>Edit Question</DialogTitle>
			<DialogContent>
				<Stack spacing={3} sx={{pt: 1}}>
					<Box>
						<Typography variant="caption" className={classes.typeLabel}>
							Question type
						</Typography>
						<Typography>{formatQuestionType(question.__type)}</Typography>
					</Box>
					{!isEditable ? (
						<Alert severity="info">
							This question type cannot be edited here yet.
						</Alert>
					) : (
						<>
							<TextField
								label="Question text"
								value={title}
								onChange={(event) => setTitle(event.target.value)}
								fullWidth
								required
							/>
							<TextField
								label="Hint"
								value={hint}
								onChange={(event) => setHint(event.target.value)}
								fullWidth
								multiline
								minRows={2}
							/>
							{questionType === "boolean_choice_questions" ? (
								<Box>
									<Typography fontWeight={600} sx={{mb: 1}}>
										Correct answer
									</Typography>
									<Stack direction="row" spacing={2} alignItems="center">
										<Typography>False</Typography>
										<Switch
											checked={boolAnswer}
											onChange={(event) => setBoolAnswer(event.target.checked)}
											inputProps={{"aria-label": "True or false answer"}}
										/>
										<Typography>True</Typography>
									</Stack>
								</Box>
							) : (
								optionEditor
							)}
						</>
					)}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={pending}>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={() => void handleSave()}
					disabled={pending || !isEditable}
				>
					{pending ? "Saving..." : "Save Question"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default EditQuizQuestionModal;
